
/**
 * @typedef {Object} SwatchCarouselOptions
 * @property {number} [mobileMax=3]  - Max visible swatches before enabling carousel on mobile
 * @property {number} [desktopMax=3] - Max visible swatches before enabling carousel on desktop
 * @property {string} [itemSelector='.o-style-option__list-item'] - Selector for swatch <li> items inside the UL
 * @property {string} [matchDesktop='(min-width: 990px)'] - Media query to detect desktop layout
 */

/**
 * @typedef {Object} SwatchCarouselRefs
 * @property {HTMLUListElement} list - The UL that contains the swatch <li> items
 */

export class SwatchCarousel {
  /**
   * Create a SwatchCarousel instance
   * @param {SwatchCarouselRefs} refs
   * @param {SwatchCarouselOptions} [options]
   */
  constructor(refs, options = {}) {
    if (!(refs?.list instanceof HTMLUListElement)) {
      throw new Error("SwatchCarousel: refs.list must be a <ul>");
    }

    this.refs = {
      list: refs.list
    };
    this.opts = {
      mobileMax: 3,
      desktopMax: 5,
      itemSelector: ".o-style-option__list-item",
      matchDesktop: "(min-width: 990px)",
      ...options,
    };

    /** @type {HTMLDivElement|null} */
    this.shell = null;
    /** @type {HTMLDivElement|null} */
    this.viewport = null;
    /** @type {HTMLDivElement|null} */
    this.track = null;
    /** @type {HTMLButtonElement|null} */
    this.prev = null;
    /** @type {HTMLButtonElement|null} */
    this.next = null;

    /** @type {number} */
    this.current = 0;
    /** @type {HTMLDivElement[]} */
    this.slides = [];

    /** @type {ResizeObserver|null} */
    this.ro = null;
    this.mql = window.matchMedia(this.opts.matchDesktop);

    /** @type {"plain"|"carousel"} */
    this.mode = "plain";
    this.boundArrows = false;

    this.update = this.update.bind(this);
  }

  /** Initialize observers and render the correct mode. */
  init() {
    this.ro = new ResizeObserver(() => this.update());
    this.ro.observe(this.refs.list);

    this.mql.addEventListener?.("change", this.update);
    if (!this.mql.addEventListener) this.mql.addListener(this.update); // Safari fallback

    this.update();
  }

  /** Destroy observers and remove shell if present. */
  destroy() {
    this.ro?.disconnect();
    this.ro = null;

    if (this.mql.removeEventListener) {
      this.mql.removeEventListener("change", this.update);
    } else {
      this.mql.removeListener?.(this.update);
    }

    this.demoteToPlain();
  }

  /** Public: re-evaluate whether we need carousel or not. */
  update() {
    const items = this.items();
    const count = items.length;
    const isDesktop = this.mql.matches;
    const limit = isDesktop ? this.opts.desktopMax : this.opts.mobileMax;

    if (count === 0) {
      if (this.mode === "carousel") this.demoteToPlain();
      return;
    }

    const desiredMode = count > limit ? "carousel" : "plain";

    if (desiredMode === this.mode) {
      if (this.mode === "carousel") this.refreshSlides();
      return;
    }

    if (desiredMode === "carousel") {
      this.promoteToCarousel();
    } else {
      this.demoteToPlain();
    }
  }

  /** @returns {HTMLLIElement[]} */
  items() {
    return Array.from(this.refs.list.querySelectorAll(this.opts.itemSelector));
  }

  /** Switch from plain mode to carousel mode. */
  promoteToCarousel() {
    const items = this.items();
    if (!items.length) return;

    if (!this.shell) this.createShell();

    items.forEach((li) => {
      li.hidden = true;
    });

    this.refs.list.hidden = true;

    /** @type {HTMLDivElement} */ (this.track).innerHTML = "";
    this.slides = [];

    items.forEach((li, i) => {
      const slide = document.createElement("div");
      slide.className = "swatch-shell__slide";
      slide.setAttribute("aria-hidden", i === 0 ? "false" : "true");

      const frag = document.createDocumentFragment();
      while (li.firstChild) frag.appendChild(li.firstChild);
      slide.appendChild(frag);

      /** @type {HTMLDivElement} */ (this.track).appendChild(slide);
      this.slides.push(slide);
    });

    this.current = 0;
    this.bindArrowsOnce();
    this.updateUI();

    /** @type {HTMLDivElement} */ (this.shell).hidden = false;
    this.mode = "carousel";
  }

  /** Switch from carousel mode back to plain list mode. */
  demoteToPlain() {
    if (!this.shell || !this.track) {
      this.mode = "plain";
      return;
    }

    this.refs.list.hidden = false;
    const items = this.items();
    const slides = Array.from(this.track.children);

    slides.forEach((slide, i) => {
      const li = items[i];
      if (!li) return;
      const frag = document.createDocumentFragment();
      while (slide.firstChild) frag.appendChild(slide.firstChild);
      li.appendChild(frag);
      li.hidden = false;
    });

    this.shell.remove();
    this.shell = this.viewport = this.track = this.prev = this.next = null;
    this.slides = [];
    this.boundArrows = false;

    this.mode = "plain";
  }

  /** Create carousel wrapper shell with viewport, track, and arrows. */
  createShell() {
    const shell = document.createElement("div");
    shell.className = "swatch-shell";
    shell.setAttribute("data-swatch-shell", "");
    shell.hidden = true;

    shell.innerHTML = `
      <button class="swatch-shell__prev" type="button" aria-label="Previous colors">
        <svg class="icon icon-small-arrow-left" width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M6.75 12.6654L1.25 7.16537L6.75 1.66537" stroke="black" stroke-width="1.25" stroke-miterlimit="10"/>
        </svg>
      </button>
      <div class="swatch-shell__viewport">
        <div class="swatch-shell__track"></div>
      </div>
      <button class="swatch-shell__next" type="button" aria-label="Next colors">
        <svg class="icon icon-small-arrow-right" width="8" height="14" viewBox="0 0 8 14" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M1.25 12.6654L6.75 7.16537L1.25 1.66537" stroke="black" stroke-width="1.25" stroke-miterlimit="10"/>
        </svg>
      </button>
    `;

    this.refs.list.insertAdjacentElement("afterend", shell);

    this.shell = shell;
    this.viewport = shell.querySelector(".swatch-shell__viewport");
    this.track = shell.querySelector(".swatch-shell__track");
    this.prev = shell.querySelector(".swatch-shell__prev");
    this.next = shell.querySelector(".swatch-shell__next");
  }

  /** Refresh slides list and clamp current index. */
  refreshSlides() {
    if (!this.track) return;

    this.slides = Array.from(this.track.children).filter(
      (el) => el instanceof HTMLDivElement
    );
    this.current = Math.max(0, Math.min(this.current, this.slides.length - 1));
    this.updateUI();
  }

  /** Bind arrow button events once. */
  bindArrowsOnce() {
    if (this.boundArrows) return;
    if (!this.prev || !this.next) return;

    this.prev.addEventListener("click", (e) => {
      e.preventDefault();
      if (this.current > 0) {
        this.current -= 1;
        this.updateUI();
      }
    });

    this.next.addEventListener("click", (e) => {
      e.preventDefault();
      if (this.current < this.slides.length - 1) {
        this.current += 1;
        this.updateUI();
      }
    });

    this.boundArrows = true;
  }

  /** Update carousel UI: position, aria attributes, arrow states. */
  updateUI() {
    if (!this.track || this.slides.length === 0) return;

    const isDesktop = this.mql.matches;
    const visibleCount = isDesktop ? this.opts.desktopMax : this.opts.mobileMax;


    const maxStart = Math.max(0, this.slides.length - visibleCount);
    this.current = Math.max(0, Math.min(this.current, maxStart));

    const slideW = this.slides[0]?.offsetWidth || 36;
    const gapStr =
      getComputedStyle(this.track).columnGap ||
      getComputedStyle(this.track).gap ||
      "0";
    const gap = parseFloat(gapStr) || 0;

    const offset = (slideW + gap) * this.current;
    this.track.style.transform = `translateX(-${offset}px)`;

    this.slides.forEach((s, i) => {
      const inView = i >= this.current && i < this.current + visibleCount;
      s.setAttribute("aria-hidden", String(!inView));
    });

    if (this.prev) this.prev.disabled = this.current === 0;
    if (this.next) this.next.disabled = this.current >= maxStart;
  }
}
